import axios from 'axios';
import { ensureFreshGhAccessToken, refreshGhAccessToken } from '@/lib/ghAuth';
import { ensureDevSession, isDevAutoLogin } from '@/lib/devAuth';
import { useAuthStore } from '@/stores/AuthStore';

const baseURL = import.meta.env.VITE_API_URL ?? '';

export const networkClient = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
});

networkClient.interceptors.request.use(async config => {
  if (isDevAutoLogin()) {
    await ensureDevSession();
  } else {
    await ensureFreshGhAccessToken();
  }
  const token = useAuthStore.getState().token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

networkClient.interceptors.response.use(
  response => response,
  async error => {
    if (!axios.isAxiosError(error) || !error.config) {
      return Promise.reject(error);
    }

    const originalRequest = error.config as typeof error.config & { _retry?: boolean };
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    if (isDevAutoLogin()) {
      const ok = await ensureDevSession(true);
      const devToken = useAuthStore.getState().token;
      if (!ok || !devToken) {
        return Promise.reject(error);
      }
      originalRequest.headers = originalRequest.headers ?? {};
      originalRequest.headers.Authorization = `Bearer ${devToken}`;
      return networkClient(originalRequest);
    }

    if (!useAuthStore.getState().token) {
      return Promise.reject(error);
    }

    const refreshedToken = await refreshGhAccessToken({ interactive: false });
    if (!refreshedToken) {
      return Promise.reject(error);
    }

    const token = useAuthStore.getState().token;
    if (!token) {
      return Promise.reject(error);
    }

    originalRequest.headers = originalRequest.headers ?? {};
    originalRequest.headers.Authorization = `Bearer ${token}`;
    return networkClient(originalRequest);
  }
);

// Axios builds these from the transport, not from the API, so surfacing them
// shows players "Request failed with status code 409" instead of real copy.
const isAxiosGeneratedMessage = (message?: string): boolean =>
  !message || /^Request failed with status code \d+$/.test(message) || message === 'Network Error';

export const getErrorMessage = (
  error: unknown,
  fallback = 'Something went wrong. Please try again.'
): string => {
  if (axios.isAxiosError<{ message?: string; error?: string }>(error)) {
    const apiMessage = error.response?.data?.message ?? error.response?.data?.error;
    if (apiMessage) return apiMessage;
    return isAxiosGeneratedMessage(error.message) ? fallback : error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return fallback;
};
